/**
 * Content Enricher Service
 * 
 * AI-powered destination content enrichment using:
 * - Wikivoyage API (travel guides)
 * - Wikipedia API (history, geography)
 * - OpenStreetMap Overpass API (POIs)
 * - Gemini AI (synthesis & translation)
 */

import { GoogleGenAI } from "@google/genai";
import type {
    DestinationContent,
    DestinationAttraction,
    DestinationRestaurant,
    DestinationActivity
} from '../types';

// Initialize Gemini AI
const apiKey = import.meta.env.VITE_GEMINI_API_KEY || import.meta.env.VITE_API_KEY;
const ai = apiKey ? new GoogleGenAI({ apiKey }) : null;

// Destination coordinates for OSM queries
const DESTINATION_COORDS: Record<string, { lat: number; lon: number; wikivoyageName: string; wikipediaName: string }> = {
    'Gran Canaria': { lat: 27.9202, lon: -15.5474, wikivoyageName: 'Gran_Canaria', wikipediaName: 'Gran_Canaria' },
    'Antalya': { lat: 36.8969, lon: 30.7133, wikivoyageName: 'Antalya', wikipediaName: 'Antalya' },
    'Rhodes': { lat: 36.4349, lon: 28.2176, wikivoyageName: 'Rhodes', wikipediaName: 'Rhodes' },
    'Rhodos': { lat: 36.4349, lon: 28.2176, wikivoyageName: 'Rhodes', wikipediaName: 'Rhodes' },
    'Hurghada': { lat: 27.2579, lon: 33.8116, wikivoyageName: 'Hurghada', wikipediaName: 'Hurghada' },
    'Dubai': { lat: 25.2048, lon: 55.2708, wikivoyageName: 'Dubai', wikipediaName: 'Dubai' },
    'Mallorca': { lat: 39.6953, lon: 3.0176, wikivoyageName: 'Mallorca', wikipediaName: 'Mallorca' },
    'Gambia': { lat: 13.4549, lon: -16.5790, wikivoyageName: 'The_Gambia', wikipediaName: 'The_Gambia' },
    'The Gambia': { lat: 13.4549, lon: -16.5790, wikivoyageName: 'The_Gambia', wikipediaName: 'The_Gambia' },
    'Kos': { lat: 36.8935, lon: 26.9773, wikivoyageName: 'Kos', wikipediaName: 'Kos' },
    'Bodrum': { lat: 37.0344, lon: 27.4305, wikivoyageName: 'Bodrum', wikipediaName: 'Bodrum' },
    'Egypt': { lat: 26.8206, lon: 30.8025, wikivoyageName: 'Egypt', wikipediaName: 'Egypt' },
    'Thailand': { lat: 15.8700, lon: 100.9925, wikivoyageName: 'Thailand', wikipediaName: 'Thailand' },
    // Canary Islands
    'Tenerife': { lat: 28.2916, lon: -16.6291, wikivoyageName: 'Tenerife', wikipediaName: 'Tenerife' },
    'Lanzarote': { lat: 29.0469, lon: -13.5899, wikivoyageName: 'Lanzarote', wikipediaName: 'Lanzarote' },
    'Fuerteventura': { lat: 28.3587, lon: -14.0537, wikivoyageName: 'Fuerteventura', wikipediaName: 'Fuerteventura' },
    // Greek Islands
    'Crete': { lat: 35.2401, lon: 24.9093, wikivoyageName: 'Crete', wikipediaName: 'Crete' },
    'Kreta': { lat: 35.2401, lon: 24.9093, wikivoyageName: 'Crete', wikipediaName: 'Crete' },
    'Corfu': { lat: 39.6243, lon: 19.9217, wikivoyageName: 'Corfu', wikipediaName: 'Corfu' },
    'Korfu': { lat: 39.6243, lon: 19.9217, wikivoyageName: 'Corfu', wikipediaName: 'Corfu' },
    'Santorini': { lat: 36.3932, lon: 25.4615, wikivoyageName: 'Santorini', wikipediaName: 'Santorini' },
    'Zakynthos': { lat: 37.7870, lon: 20.8979, wikivoyageName: 'Zakynthos', wikipediaName: 'Zakynthos' },
    // Turkey
    'Alanya': { lat: 36.5437, lon: 31.9993, wikivoyageName: 'Alanya', wikipediaName: 'Alanya' },
    'Side': { lat: 36.7666, lon: 31.3903, wikivoyageName: 'Side_(Turkey)', wikipediaName: 'Side_(Turkey)' },
    'Marmaris': { lat: 36.8550, lon: 28.2743, wikivoyageName: 'Marmaris', wikipediaName: 'Marmaris' },
    'Turkey': { lat: 38.9637, lon: 35.2433, wikivoyageName: 'Turkey', wikipediaName: 'Turkey' },
    'Turkiet': { lat: 38.9637, lon: 35.2433, wikivoyageName: 'Turkey', wikipediaName: 'Turkey' },
    // Egypt
    'Sharm El Sheikh': { lat: 27.9158, lon: 34.3300, wikivoyageName: 'Sharm_el-Sheikh', wikipediaName: 'Sharm_el-Sheikh' },
    'Marsa Alam': { lat: 25.0660, lon: 34.8980, wikivoyageName: 'Marsa_Alam', wikipediaName: 'Marsa_Alam' },
    // Cyprus
    'Cyprus': { lat: 35.1264, lon: 33.4299, wikivoyageName: 'Cyprus', wikipediaName: 'Cyprus' },
    'Cypern': { lat: 35.1264, lon: 33.4299, wikivoyageName: 'Cyprus', wikipediaName: 'Cyprus' },
    // Countries
    'Greece': { lat: 39.0742, lon: 21.8243, wikivoyageName: 'Greece', wikipediaName: 'Greece' },
    'Grekland': { lat: 39.0742, lon: 21.8243, wikivoyageName: 'Greece', wikipediaName: 'Greece' },
    'Spain': { lat: 40.4168, lon: -3.7038, wikivoyageName: 'Spain', wikipediaName: 'Spain' },
    'Spanien': { lat: 40.4168, lon: -3.7038, wikivoyageName: 'Spain', wikipediaName: 'Spain' },
    // Indian Ocean
    'Mauritius': { lat: -20.3484, lon: 57.5522, wikivoyageName: 'Mauritius', wikipediaName: 'Mauritius' },
    'Maldives': { lat: 3.2028, lon: 73.2207, wikivoyageName: 'Maldives', wikipediaName: 'Maldives' },
    'Maldiverna': { lat: 3.2028, lon: 73.2207, wikivoyageName: 'Maldives', wikipediaName: 'Maldives' },
    'Sri Lanka': { lat: 7.8731, lon: 80.7718, wikivoyageName: 'Sri_Lanka', wikipediaName: 'Sri_Lanka' },
    'Zanzibar': { lat: -6.1659, lon: 39.2026, wikivoyageName: 'Zanzibar', wikipediaName: 'Zanzibar' },
    // Southeast Asia
    'Phuket': { lat: 7.9519, lon: 98.3381, wikivoyageName: 'Phuket', wikipediaName: 'Phuket' },
    'Bali': { lat: -8.3405, lon: 115.0920, wikivoyageName: 'Bali', wikipediaName: 'Bali' },
    'Vietnam': { lat: 14.0583, lon: 108.2772, wikivoyageName: 'Vietnam', wikipediaName: 'Vietnam' },
    // Africa
    'Morocco': { lat: 31.7917, lon: -7.0926, wikivoyageName: 'Morocco', wikipediaName: 'Morocco' },
    'Marocko': { lat: 31.7917, lon: -7.0926, wikivoyageName: 'Morocco', wikipediaName: 'Morocco' },
    'Cape Verde': { lat: 16.5388, lon: -23.0418, wikivoyageName: 'Cape_Verde', wikipediaName: 'Cape_Verde' },
    'Kap Verde': { lat: 16.5388, lon: -23.0418, wikivoyageName: 'Cape_Verde', wikipediaName: 'Cape_Verde' },
    'Tunisia': { lat: 33.8869, lon: 9.5375, wikivoyageName: 'Tunisia', wikipediaName: 'Tunisia' },
    'Tunisien': { lat: 33.8869, lon: 9.5375, wikivoyageName: 'Tunisia', wikipediaName: 'Tunisia' },
    // Caribbean & Americas
    'Mexico': { lat: 23.6345, lon: -102.5528, wikivoyageName: 'Mexico', wikipediaName: 'Mexico' },
    'Dominican Republic': { lat: 18.7357, lon: -70.1627, wikivoyageName: 'Dominican_Republic', wikipediaName: 'Dominican_Republic' },
    'Dominikanska Republiken': { lat: 18.7357, lon: -70.1627, wikivoyageName: 'Dominican_Republic', wikipediaName: 'Dominican_Republic' },
    'Cuba': { lat: 21.5218, lon: -77.7812, wikivoyageName: 'Cuba', wikipediaName: 'Cuba' },
    'Jamaica': { lat: 18.1096, lon: -77.2975, wikivoyageName: 'Jamaica', wikipediaName: 'Jamaica' },
    // Balearic Islands
    'Ibiza': { lat: 38.9067, lon: 1.4206, wikivoyageName: 'Ibiza', wikipediaName: 'Ibiza' },
    'Menorca': { lat: 39.9496, lon: 4.1107, wikivoyageName: 'Menorca', wikipediaName: 'Menorca' }
};


interface RawContent {
    wikivoyage: string;
    wikipedia: string;
    pois: OSMPoi[];
}

interface OSMPoi {
    name: string;
    type: string;
    lat: number;
    lon: number;
    tags: Record<string, string>;
}

interface EnrichmentResult {
    success: boolean;
    attractions: DestinationAttraction[];
    restaurants: DestinationRestaurant[];
    activities: DestinationActivity[];
    practicalTips: { en: string[]; sv: string[] };
    hiddenGems: { en: string[]; sv: string[] };
    rawContent?: string;
    error?: string;
}

/**
 * Resolve coordinates for a destination name.
 * Handles names with country suffix (e.g. "Gran Canaria, Spain" → lookups "Gran Canaria")
 */
const resolveCoords = (destination: string) => {
    // Direct match first
    if (DESTINATION_COORDS[destination]) return DESTINATION_COORDS[destination];
    // Try without country suffix: "Gran Canaria, Spain" → "Gran Canaria"
    const baseName = destination.split(',')[0].trim();
    if (DESTINATION_COORDS[baseName]) return DESTINATION_COORDS[baseName];
    // Try country part: "Gran Canaria, Spain" → "Spain"
    const countryPart = destination.split(',').slice(1).join(',').trim();
    if (countryPart && DESTINATION_COORDS[countryPart]) return DESTINATION_COORDS[countryPart];
    return null;
};

/**
 * Fetch content from Wikivoyage
 */
export const fetchWikivoyageContent = async (destination: string): Promise<string> => {
    const coords = resolveCoords(destination);
    const pageName = coords?.wikivoyageName || destination.split(',')[0].trim().replace(/ /g, '_');

    try {
        const response = await fetch(
            `https://en.wikivoyage.org/w/api.php?` +
            `action=query&prop=extracts&titles=${encodeURIComponent(pageName)}` +
            `&format=json&origin=*&explaintext=true&exsectionformat=plain`
        );

        if (!response.ok) throw new Error(`Wikivoyage API error: ${response.status}`);

        const data = await response.json();
        const pages = data.query?.pages;
        if (!pages) return '';

        const page = Object.values(pages)[0] as any;
        return page?.extract || '';
    } catch (error) {
        console.error('[ContentEnricher] Wikivoyage fetch failed:', error);
        return '';
    }
};

/**
 * Fetch content from Wikipedia
 */
export const fetchWikipediaContent = async (destination: string): Promise<string> => {
    const coords = resolveCoords(destination);
    const pageName = coords?.wikipediaName || destination.split(',')[0].trim().replace(/ /g, '_');

    try {
        const response = await fetch(
            `https://en.wikipedia.org/w/api.php?` +
            `action=query&prop=extracts&titles=${encodeURIComponent(pageName)}` +
            `&format=json&origin=*&explaintext=true&exintro=true`
        );

        if (!response.ok) throw new Error(`Wikipedia API error: ${response.status}`);

        const data = await response.json();
        const pages = data.query?.pages;
        if (!pages) return '';

        const page = Object.values(pages)[0] as any;
        return page?.extract || '';
    } catch (error) {
        console.error('[ContentEnricher] Wikipedia fetch failed:', error);
        return '';
    }
};

/**
 * Fetch POIs from OpenStreetMap Overpass API
 */
export const fetchOSMPois = async (destination: string, radius: number = 10000): Promise<OSMPoi[]> => {
    const coords = resolveCoords(destination);
    if (!coords) {
        console.warn(`[ContentEnricher] No coordinates for destination: ${destination}`);
        return [];
    }

    const { lat, lon } = coords;

    const query = `
        [out:json][timeout:30];
        (
            node["tourism"="attraction"](around:${radius},${lat},${lon});
            node["tourism"="museum"](around:${radius},${lat},${lon});
            node["tourism"="viewpoint"](around:${radius},${lat},${lon});
            node["amenity"="restaurant"]["cuisine"](around:${radius},${lat},${lon});
            node["historic"](around:${radius},${lat},${lon});
            node["natural"="beach"](around:${radius},${lat},${lon});
        );
        out body 50;
    `;

    try {
        const response = await fetch('https://overpass-api.de/api/interpreter', {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: query
        });

        if (!response.ok) throw new Error(`Overpass API error: ${response.status}`);

        const data = await response.json();

        return (data.elements || []).map((el: any) => ({
            name: el.tags?.name || el.tags?.['name:en'] || 'Unnamed',
            type: el.tags?.tourism || el.tags?.amenity || el.tags?.historic || el.tags?.natural || 'poi',
            lat: el.lat,
            lon: el.lon,
            tags: el.tags || {}
        })).filter((poi: OSMPoi) => poi.name !== 'Unnamed');
    } catch (error) {
        console.error('[ContentEnricher] OSM fetch failed:', error);
        return [];
    }
};

/**
 * Synthesize content using Gemini AI
 */
export const synthesizeWithAI = async (
    destination: string,
    rawContent: RawContent
): Promise<EnrichmentResult> => {
    if (!ai) {
        return {
            success: false,
            attractions: [],
            restaurants: [],
            activities: [],
            practicalTips: { en: [], sv: [] },
            hiddenGems: { en: [], sv: [] },
            error: 'AI API key not configured'
        };
    }

    const poisSummary = rawContent.pois.slice(0, 20).map(p =>
        `${p.name} (${p.type}): ${Object.entries(p.tags).slice(0, 3).map(([k, v]) => `${k}=${v}`).join(', ')}`
    ).join('\n');

    const prompt = `You are a professional travel content writer. Create rich, engaging travel content for ${destination}.

Based on this research:

=== WIKIVOYAGE CONTENT ===
${rawContent.wikivoyage.slice(0, 4000)}

=== WIKIPEDIA CONTENT ===
${rawContent.wikipedia.slice(0, 2000)}

=== POINTS OF INTEREST FROM OPENSTREETMAP ===
${poisSummary}

Generate structured JSON with EXACTLY this format (no markdown, pure JSON):
{
    "attractions": [
        {
            "id": "unique-id",
            "title": { "en": "English name", "sv": "Swedish name" },
            "description": { "en": "2-3 sentence description", "sv": "Swedish translation" },
            "category": "landmark|museum|nature|beach|historic",
            "duration": "1-2 hours",
            "price": "Free|€10|€20"
        }
    ],
    "restaurants": [
        {
            "id": "unique-id",
            "name": "Restaurant Name",
            "description": { "en": "What makes it special", "sv": "Swedish translation" },
            "cuisine": "Local|Mediterranean|Seafood|International",
            "priceRange": "$|$$|$$$|$$$$"
        }
    ],
    "activities": [
        {
            "id": "unique-id",
            "title": { "en": "Activity name", "sv": "Swedish" },
            "description": { "en": "Description", "sv": "Swedish" },
            "category": "tour|water-sports|cultural|adventure|relaxation",
            "duration": "Half day|Full day|2 hours",
            "price": "€30"
        }
    ],
    "practicalTips": {
        "en": ["Tip 1", "Tip 2", "Tip 3", "Tip 4", "Tip 5"],
        "sv": ["Swedish tip 1", "Swedish tip 2", "Swedish tip 3", "Swedish tip 4", "Swedish tip 5"]
    },
    "hiddenGems": {
        "en": ["Hidden gem 1 with description", "Hidden gem 2"],
        "sv": ["Swedish hidden gem 1", "Swedish hidden gem 2"]
    }
}

Requirements:
- Generate 5-7 attractions
- Generate 4-5 restaurants (use local cuisine names)
- Generate 4-5 activities
- Generate 5 practical tips
- Generate 3 hidden gems (off-the-beaten-path suggestions)
- All text must be in BOTH English and Swedish
- Be specific to ${destination}, use real place names
- Output ONLY valid JSON, no extra text`;

    try {
        console.log('[ContentEnricher] Calling Gemini AI for synthesis...');

        const result = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt
        });

        // GenerateContentResponse has text property directly (not response.text())
        let responseText = '';
        try {
            // The @google/genai SDK returns text directly on the result
            if (result && typeof result.text === 'string') {
                responseText = result.text;
            } else if (result && result.candidates?.[0]?.content?.parts?.[0]?.text) {
                responseText = result.candidates[0].content.parts[0].text;
            } else {
                // Fallback: stringify the result to see what we got
                console.warn('[ContentEnricher] Unexpected result structure:', JSON.stringify(result).substring(0, 500));
                responseText = '';
            }
        } catch (extractError) {
            console.error('[ContentEnricher] Error extracting text:', extractError);
        }

        if (!responseText) {
            throw new Error('Empty response from Gemini AI');
        }

        // Extract JSON from response (handle markdown code blocks)
        let jsonStr = responseText;
        const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (jsonMatch) {
            jsonStr = jsonMatch[1];
        }

        const parsed = JSON.parse(jsonStr.trim());

        console.log('[ContentEnricher] AI synthesis complete');

        return {
            success: true,
            attractions: parsed.attractions || [],
            restaurants: parsed.restaurants || [],
            activities: parsed.activities || [],
            practicalTips: parsed.practicalTips || { en: [], sv: [] },
            hiddenGems: parsed.hiddenGems || { en: [], sv: [] },
            rawContent: responseText
        };
    } catch (error: any) {
        console.error('[ContentEnricher] AI synthesis failed:', error);
        return {
            success: false,
            attractions: [],
            restaurants: [],
            activities: [],
            practicalTips: { en: [], sv: [] },
            hiddenGems: { en: [], sv: [] },
            error: error.message || 'AI synthesis failed'
        };
    }
};

/**
 * Main enrichment function - orchestrates all data sources
 */
export const enrichDestinationContent = async (
    destination: string,
    existingContent?: Partial<DestinationContent>
): Promise<EnrichmentResult> => {
    console.log(`[ContentEnricher] Starting enrichment for: ${destination}`);

    // Fetch from all sources in parallel
    const [wikivoyage, wikipedia, pois] = await Promise.all([
        fetchWikivoyageContent(destination),
        fetchWikipediaContent(destination),
        fetchOSMPois(destination)
    ]);

    console.log(`[ContentEnricher] Fetched: Wikivoyage=${wikivoyage.length}chars, Wikipedia=${wikipedia.length}chars, POIs=${pois.length}`);

    if (!wikivoyage && !wikipedia && pois.length === 0) {
        return {
            success: false,
            attractions: [],
            restaurants: [],
            activities: [],
            practicalTips: { en: [], sv: [] },
            hiddenGems: { en: [], sv: [] },
            error: 'No content found from any source'
        };
    }

    // Synthesize with AI
    const result = await synthesizeWithAI(destination, {
        wikivoyage,
        wikipedia,
        pois
    });

    return result;
};

/**
 * Quick test function for a single destination
 */
export const testEnrichment = async (destination: string = 'Mallorca'): Promise<void> => {
    console.log(`\n=== Testing Content Enrichment for ${destination} ===\n`);

    const result = await enrichDestinationContent(destination);

    if (result.success) {
        console.log('✅ Enrichment successful!');
        console.log(`   Attractions: ${result.attractions.length}`);
        console.log(`   Restaurants: ${result.restaurants.length}`);
        console.log(`   Activities: ${result.activities.length}`);
        console.log(`   Tips: ${result.practicalTips.en.length}`);
        console.log(`   Hidden Gems: ${result.hiddenGems.en.length}`);
    } else {
        console.log('❌ Enrichment failed:', result.error);
    }
};

export default {
    enrichDestinationContent,
    fetchWikivoyageContent,
    fetchWikipediaContent,
    fetchOSMPois,
    synthesizeWithAI,
    testEnrichment,
    DESTINATION_COORDS
};
